 /**
 * Eyewear Features Video Handler
 * 
 * This script fixes video playback in the eyewear features section when product variants change.
 * It works by reinitializing video functionality whenever the content is updated through AJAX.
 */

// Initialize video functionality for all videos in the section
function initializeEyewearVideos() {
  console.log('Initializing eyewear videos');
  
  // Clear any existing observers to avoid duplicates
  if (window.eyewearVideoObserver) {
    window.eyewearVideoObserver.disconnect();
  }
  
  // Set up intersection observer for autoplay videos
  const autoplayVids = document.querySelectorAll('video[data-autoplay="true"]');
  
  if (autoplayVids.length === 0) {
    console.log('No autoplay videos found - checking again in 100ms');
    // If no videos found, wait and try again as the DOM might still be updating
    setTimeout(() => {
      const retryVids = document.querySelectorAll('video[data-autoplay="true"]');
      if (retryVids.length > 0) {
        console.log('Found videos on retry:', retryVids.length);
        setupIntersectionObserver(retryVids);
      } else {
        console.log('Still no videos found after retry');
      }
    }, 100);
  } else {
    console.log('Found videos immediately:', autoplayVids.length);
    setupIntersectionObserver(autoplayVids);
  }
}

function setupIntersectionObserver(videoElements) {
  const config = {
    rootMargin: '-75px -75px',
    threshold: 0
  };
  
  const callback = (entries) => {
    entries.forEach((entry) => {
      const container = entry.target.closest('.video-with-controls');
      if (!container) return;
      
      const video = entry.target;
      const playButton = container.querySelector('.play');
      const pauseButton = container.querySelector('.pause');
      
      if (!playButton || !pauseButton) return;
      
      if (!entry.isIntersecting) {
        video.pause();
        console.log('Video paused as it left viewport');
      } else {
        if (!pauseButton.classList.contains('hidden')) {
          video.play().catch(err => {
            console.log('Could not play video automatically:', err.message);
          });
          console.log('Video played as it entered viewport');
        }
      }
    });
  };

  // Create new observer and store reference globally
  window.eyewearVideoObserver = new IntersectionObserver(callback, config);

  if (videoElements && videoElements.length > 0) {
    videoElements.forEach(element => {
      window.eyewearVideoObserver.observe(element);
      console.log('Added observer to video:', element.id || 'unnamed video');
    });
  }
}

// Handle video control clicks
function setupVideoControls() {
  // Use event delegation to handle newly added videos
  document.removeEventListener('click', handleVideoControlClick);
  document.addEventListener('click', handleVideoControlClick);
}

function handleVideoControlClick(e) {
  if (!e.target.closest('.pause') && !e.target.closest('.play') && !e.target.closest('.mute-wrap')) return;
  
  const container = e.target.closest('.video-with-controls');
  if (!container) return;
  
  // Look for both selectors that might be used for videos
  const video = container.querySelector('.hero-banner-video__video') || container.querySelector('video');
  if (!video) {
    console.error('Video element not found in container', container);
    return;
  }
  
  const playButton = container.querySelector('.play');
  const pauseButton = container.querySelector('.pause');
  const muteButton = container.querySelector('.mute-wrap');
  
  if (!playButton || !pauseButton) {
    console.error('Play/pause buttons not found in container', container);
    return;
  }

  if (e.target.closest('button.pause')) {
    if (video.paused == false) {
      video.pause();
      playButton.classList.remove('hidden');
      pauseButton.classList.add('hidden');
    }
  }
  
  if (e.target.closest('button.play')) {
    if (video.paused == true) {
      video.play().catch(err => {
        console.log('Could not play video on click:', err.message);
      });
      playButton.classList.add('hidden');
      pauseButton.classList.remove('hidden');
    }
  }
  
  if (e.target.closest('.mute-wrap')) {
    if (!muteButton) {
      console.error('Mute button not found in container', container);
      return;
    }
    
    const muteIcon = muteButton.querySelector('.icon-mute');
    const unmuteIcon = muteButton.querySelector('.icon-unmute');
    
    if (!muteIcon || !unmuteIcon) {
      console.error('Mute/unmute icons not found in container', container);
      return;
    }
    
    if (video.muted == false) {
      video.muted = true;
      muteIcon.classList.remove('hidden');
      unmuteIcon.classList.add('hidden');
    } else {
      video.muted = false;
      muteIcon.classList.add('hidden');
      unmuteIcon.classList.remove('hidden');
    }
  }
}

// Listen for variant changes
function setupVariantChangeListeners() {
  // Set up event listeners specific to your product-info custom element implementation
  document.addEventListener('variant_updated', function(event) {
    console.log('variant_updated event detected - reinitializing videos');
    // Give the DOM time to update before reinitializing
    setTimeout(() => {
      initializeEyewearVideos();
    }, 250);
  });
  
  // Custom events from variant selectors (specific to Shopify themes)
  [
    'variant:changed',
    'product:variant:change',
    'product-info:changed',
    'product-info:loaded'
  ].forEach(eventName => {
    document.addEventListener(eventName, function(event) {
      console.log(`${eventName} event detected - reinitializing videos`);
      setTimeout(() => {
        initializeEyewearVideos();
      }, 250);
    });
  });
  
  // Monitor DOM changes in the eyewear features section
  const eyewearFeaturesSection = document.querySelector('.section-eyewear-features');
  if (eyewearFeaturesSection) {
    const observer = new MutationObserver((mutations) => {
      let shouldReinitialize = false;
      
      for (const mutation of mutations) {
        // Check for added or removed elements
        if (mutation.type === 'childList') {
          const videoRelatedChange = Array.from(mutation.addedNodes)
            .concat(Array.from(mutation.removedNodes))
            .some(node => {
              if (node.nodeType !== Node.ELEMENT_NODE) return false;
              return node.tagName === 'VIDEO' || 
                    node.querySelector('video') || 
                    node.classList.contains('video-with-controls') ||
                    node.querySelector('.video-with-controls');
            });
            
          if (videoRelatedChange) {
            shouldReinitialize = true;
            break;
          }
        }
      }
      
      if (shouldReinitialize) {
        console.log('Video-related DOM changes detected - reinitializing videos');
        setTimeout(() => {
          initializeEyewearVideos();
        }, 250);
      }
    });
    
    observer.observe(eyewearFeaturesSection, { 
      childList: true, 
      subtree: true
    });
  }
}

// Initialize everything on DOMContentLoaded
document.addEventListener("DOMContentLoaded", () => {
  console.log('Eyewear features video handler initialized');
  // Set up video controls
  setupVideoControls();
  
  // Initialize videos
  initializeEyewearVideos();
  
  // Set up variant change listeners
  setupVariantChangeListeners();
});

// Handle Shopify's section:load event for theme editor and section rendering
document.addEventListener('shopify:section:load', function(event) {
  if (event.detail.sectionId && event.detail.sectionId.includes('eyewear-features')) {
    console.log('Eyewear features section loaded in theme editor');
    setupVideoControls();
    initializeEyewearVideos();
    setupVariantChangeListeners();
  }
});

// Re-initialize when the section is selected in the theme editor
document.addEventListener('shopify:section:select', function(event) {
  if (event.detail.sectionId && event.detail.sectionId.includes('eyewear-features')) {
    console.log('Eyewear features section selected in theme editor');
    initializeEyewearVideos();
  }
});